// Change Password Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Container,
  Paper,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import authService from '../../services/auth';

const ChangePassword = () => {
  const navigate = useNavigate();
  
  const [password, setPassword] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check admin access on component mount
  useEffect(() => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    
    const user = authService.getUser();
    const isAdmin = user && (user.role_name === 'Admin' || user.role === 'Admin');
    
    if (!isAdmin) {
      // Redirect non-admin users to profile page
      navigate('/dashboard/profile');
      return;
    }
    
    setLoading(false);
  }, [navigate]);

  // Update form validity whenever password state changes
  useEffect(() => {
    const isValid = password.current_password && 
                   password.new_password && 
                   password.confirm_password &&
                   password.new_password === password.confirm_password &&
                   password.new_password.length >= 6 &&
                   password.current_password !== password.new_password;
    setIsFormValid(isValid);
  }, [password]);

  const handlePasswordChange = (field, value) => {
    setPassword(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear any existing errors when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleShowPassword = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validation
      if (!password.current_password) {
        setError('Current password is required');
        return;
      }

      if (!password.new_password) {
        setError('New password is required');
        return;
      }

      if (password.new_password.length < 6) {
        setError('New password must be at least 6 characters long');
        return;
      }

      if (password.new_password !== password.confirm_password) {
        setError('New password and confirm password do not match');
        return;
      }

      if (password.current_password === password.new_password) {
        setError('New password must be different from current password');
        return;
      }

      await apiService.put('/user/password', {
        current_password: password.current_password,
        new_password: password.new_password,
        confirm_password: password.confirm_password,
      });

      setSuccess('Password changed successfully!');
      
      // Clear form after successful change
      setPassword({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });

      // Redirect to profile after 2 seconds
      setTimeout(() => {
        navigate('/dashboard/profile');
      }, 2000);

    } catch (error) {
      console.error('Error changing password:', error);
      
      // Get the error message from the error object
      const errorMessage = error.message || '';

      // Prefer server-side validation details (422) when available
      const validationErrors = error?.responseData?.data?.errors;
      if (validationErrors && typeof validationErrors === 'object') {
        const messages = Object.values(validationErrors).filter(Boolean);
        if (messages.length) {
          setError(messages.join(' '));
          return;
        }
      }
      
      // Handle specific error cases with user-friendly messages
      if (errorMessage.toLowerCase().includes('current password is incorrect')) {
        setError('Current password is incorrect. Please check and try again.');
      } else if (errorMessage.toLowerCase().includes('new password must be at least')) {
        setError(errorMessage);
      } else if (errorMessage.toLowerCase().includes('current password and new password are required')) {
        setError('Please fill in all password fields.');
      } else if (errorMessage.toLowerCase().includes('user not found')) {
        setError('User account not found. Please log in again.');
      } else if (errorMessage.toLowerCase().includes('failed to update password')) {
        setError('Failed to update password. Please try again.');
      } else if (errorMessage) {
        setError(errorMessage);
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };


  // Show loading while checking access
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Checking access...</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <LockIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Change Password
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Please enter your current password and choose a new secure password.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Current Password */}
              <TextField
                fullWidth
                autoComplete="current-password"
                label="Current Password"
                type={showPasswords.current ? 'text' : 'password'}
                value={password.current_password}
                onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleShowPassword('current')}
                        edge="end"
                      >
                        {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* New Password */}
              <TextField
                fullWidth
                autoComplete="new-password"
                label="New Password"
                type={showPasswords.new ? 'text' : 'password'}
                value={password.new_password}
                onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                helperText="Password must be at least 6 characters long"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleShowPassword('new')}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm New Password */}
              <TextField
                fullWidth
                autoComplete="new-password"
                label="Confirm New Password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={password.confirm_password}
                onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleShowPassword('confirm')}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard/profile')}
                  disabled={saving}
                  sx={{ borderRadius: 2 }}
                >
                  Back to Profile
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleChangePassword}
                  disabled={saving || !isFormValid}
                  sx={{ borderRadius: 2, flexGrow: 1 }}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default ChangePassword;
